import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { PrimaryButton } from "../components/Button";
import ExperienceGrid from "../components/ExperienceGrid";
import { updateProfile, getUserProfile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

type Experience = "Beginner" | "Intermediate" | "Professional";

const isNonEmptyString = (value?: string | null) =>
  typeof value === "string" && value.trim().length > 0;

const toNumber = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const hasBasicDetails1 = (userData: {
  name?: string | null;
  age?: number | string | null;
  sex?: string | null;
  experience?: string | null;
}) => {
  const ageNumber = toNumber(userData.age);
  return (
    isNonEmptyString(userData.name) &&
    typeof ageNumber === "number" &&
    ageNumber > 0 &&
    (userData.sex === "Male" || userData.sex === "Female") &&
    (userData.experience === "Beginner" ||
      userData.experience === "Intermediate" ||
      userData.experience === "Professional")
  );
};

const hasBasicDetails2 = (userData: {
  hasGloves?: boolean | null;
  gloveSize?: number | string | null;
  hasHandwrap?: boolean | null;
}) => {
  const gloveSizeNumber = toNumber(userData.gloveSize);
  return (
    typeof userData.hasGloves === "boolean" &&
    typeof userData.hasHandwrap === "boolean" &&
    typeof gloveSizeNumber === "number" &&
    gloveSizeNumber > 0
  );
};

export default function BasicDetails1() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [experience, setExperience] = useState<Experience>("Beginner");
  const [sex, setSex] = useState<"Male" | "Female">("Male");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) {
        setInitialLoading(false);
        return;
      }

      try {
        const response = await getUserProfile(user.id);
        const userData = response.user;

        const step1Done = hasBasicDetails1(userData);
        const step2Done = hasBasicDetails2(userData);

        if (step1Done && step2Done) {
          console.log("Basic details already present, redirecting to home");
          navigate("/student/home");
          return;
        }

        if (step1Done && !step2Done) {
          console.log("Step 1 complete, redirecting to basic-details2");
          navigate("/student/basic-details2");
          return;
        }

        // Basic details are incomplete, populate form with existing data
        setName(userData.name || "");
        setAge(userData.age ? String(userData.age) : "");
        setExperience((userData.experience as Experience) || "Beginner");
        setSex((userData.sex as "Male" | "Female") || "Male");
      } catch (error) {
        console.error("Error loading user profile:", error);
        // Set default values if profile loading fails
        setName("");
        setAge("");
        setExperience("Beginner");
        setSex("Male");
      } finally {
        setInitialLoading(false);
      }
    };

    loadUserProfile();
  }, [user?.id, navigate]);

  const handleNext = useCallback(async () => {
    if (!name || !age || !sex) {
      setError("All fields are required");
      return;
    }

    if (!user?.id) {
      setError("User not authenticated");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await updateProfile({
        _id: user.id,
        name,
        age: parseInt(age),
        sex,
        experience
      });
      navigate("/student/basic-details2");
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [name, age, sex, experience, navigate, user?.id]);

  if (initialLoading) {
    return (
      <div className="w-full bg-[#F7F7F7] min-h-[100dvh] pt-[max(12px,env(safe-area-inset-top))] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading. Please wait.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-[#F7F7F7] min-h-[100dvh] pt-[max(12px,env(safe-area-inset-top))]">
      <div className="w-full h-[100dvh] flex flex-col">
        <Navbar onBack={() => navigate(-1)} />

        {/* content (fits viewport; no scroll) */}
        <div className="px-5 pt-2">
          {/* progress + title */}
          <div className="mb-7">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-1 bg-[#D62422]" />
              <div className="w-10 h-1 bg-[#B0B0B0]" />
            </div>
            <h1 className="font-manrope font-bold text-[20px] leading-[26px]">
              Let us know about you
            </h1>
          </div>

          {/* name */}
          <div className="mb-7">
            <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
              What should trainers call you?
            </label>
            <div className="border border-[#B1B1B1] bg-white px-4 py-2.5">
              <input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full font-manrope text-[14px] text-[#2d2d2d] placeholder:text-[#717171] outline-none bg-transparent"
              />
            </div>
          </div>

          {/* experience (reused component) */}
          <ExperienceGrid
            label="Your Muaythai Experience"
            value={experience}
            onChange={setExperience}
            className="mb-7"
          />

          {/* age + sex */}
          <div className="grid grid-cols-2 gap-7">
            <div>
              <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                How old are you?
              </label>
              <div className="border border-[#B1B1B1] bg-white px-4 h-[49px] flex items-center">
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="Your Age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full font-manrope text-[14px] placeholder:text-[#717171] outline-none bg-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block font-manrope font-extrabold text-[12px] tracking-[0.05em] uppercase mb-3">
                Division (Sex)
              </label>
              <div className="flex gap-3">
                {(["Male", "Female"] as const).map((g) => {
                  const selected = sex === g;
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setSex(g)}
                      className={`w-[72px] h-[48px] border bg-white grid place-items-center ${selected ? "border-[#D62422]" : "border-[#B1B1B1]"
                        }`}
                    >
                      <span className={`text-[12px] ${selected ? "text-black" : "text-[#717171]"}`}>
                        {g === "Male" ? "M" : "F"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
        </div>

        {/* bottom button */}
        <div className="mt-auto w-full bg-white">
          <div className="mx-auto w-full px-5 py-4 pb-[max(16px,env(safe-area-inset-bottom))]">
            <PrimaryButton
              onClick={handleNext}
              disabled={loading}
              label={loading ? "UPDATING..." : "NEXT"}
              className="!w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
